// installPrompt.ts - Instalar la app en el celular ("Agregar a pantalla de inicio").
// Android/Chrome avisan con el evento `beforeinstallprompt`: se guarda para abrir la
// ventana de instalación desde un botón. En iPhone (Safari) Apple no lo permite, así
// que solo se puede mostrar una guía. Ningún navegador instala sin que la persona confirme.
import { useEffect, useState } from 'react';

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

/** Llamar al arrancar la app (main.tsx), antes de que el navegador dispare el evento */
export function captureInstallPrompt() {
    if (typeof window === 'undefined') return;
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault(); // no mostrar la barra automática: la mostramos con nuestro botón
        deferred = e as InstallEvent;
        notify();
    });
    window.addEventListener('appinstalled', () => { deferred = null; notify(); });
}

export const isStandalone = () =>
    typeof window !== 'undefined' &&
    ((window.navigator as any).standalone === true || window.matchMedia?.('(display-mode: standalone)').matches);

export const isIOS = () =>
    typeof navigator !== 'undefined' &&
    (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const isMobile = () => typeof navigator !== 'undefined' && /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || isIOS();

export function useInstallPrompt() {
    const [, force] = useState(0);
    useEffect(() => {
        const l = () => force(n => n + 1);
        listeners.add(l);
        return () => { listeners.delete(l); };
    }, []);
    return {
        /** Android/Chrome: se puede abrir la ventana de instalación */
        canPrompt: !!deferred,
        /** iPhone/iPad: solo guía manual */
        ios: isIOS() && !isStandalone(),
        installed: isStandalone(),
        mobile: isMobile(),
        prompt: async (): Promise<boolean> => {
            if (!deferred) return false;
            const e = deferred;
            await e.prompt();
            const { outcome } = await e.userChoice;
            deferred = null;
            notify();
            return outcome === 'accepted';
        },
    };
}
